class WagmadeProductCard extends HTMLElement {
  connectedCallback() {
    if (this.dataset.cardReady === 'true') return;
    this.dataset.cardReady = 'true';

    this.primaryImg = this.querySelector('.wagmade-card__image-primary');
    this.quickAddBtn = this.querySelector('.wagmade-card__quick-add-btn');
    this.sizeSelector = this.querySelector('.wagmade-card__size-selector');
    this.swatchDots = this.querySelectorAll('.wagmade-card__swatch-dot');
    this.sizeBtns = this.querySelectorAll('.wagmade-card__size-btn');

    this.variantsJsonEl = this.querySelector('[data-card-variants-json]');
    this.variants = this.variantsJsonEl ? JSON.parse(this.variantsJsonEl.textContent) : [];
    this.colorOptionIndex = parseInt(this.dataset.colorOptionIndex ?? '-1', 10);
    this.sizeOptionIndex = parseInt(this.dataset.sizeOptionIndex ?? '-1', 10);

    this.activeVariantId = this.quickAddBtn ? this.quickAddBtn.getAttribute('data-variant-id') : null;
    this.activeColor = '';
    this.activeSize = '';

    this.initSwatches();
    this.initSizeButtons();
    this.initQuickAdd();
    this.initOutsideClick();
    this.initMediaTouch();
  }

  initSwatches() {
    if (!this.swatchDots.length) return;

    const firstActive = this.querySelector('.wagmade-card__swatch-dot[data-active="true"]');
    if (firstActive) {
      this.activeColor = firstActive.getAttribute('data-color');
    } else {
      this.swatchDots[0].setAttribute('data-active', 'true');
      this.swatchDots[0].setAttribute('aria-pressed', 'true');
      this.activeColor = this.swatchDots[0].getAttribute('data-color');
    }

    this.swatchDots.forEach((swatch) => {
      swatch.setAttribute('role', 'radio');
      swatch.setAttribute('aria-pressed', swatch.getAttribute('data-active') === 'true' ? 'true' : 'false');

      swatch.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.selectSwatch(swatch);
      });
    });

    this.filterSizesByColor(this.activeColor);
    this.resolveVariant();
  }

  selectSwatch(swatch) {
    this.swatchDots.forEach((d) => {
      d.removeAttribute('data-active');
      d.setAttribute('aria-pressed', 'false');
    });
    swatch.setAttribute('data-active', 'true');
    swatch.setAttribute('aria-pressed', 'true');

    const newSrc = swatch.getAttribute('data-image-src');
    const colorVal = swatch.getAttribute('data-color');

    if (this.primaryImg && newSrc) {
      this.primaryImg.setAttribute('src', newSrc);
      this.primaryImg.setAttribute('srcset', newSrc);
    }

    this.activeColor = colorVal;
    this.filterSizesByColor(colorVal);
    this.resolveVariant();
  }

  filterSizesByColor(color) {
    if (!color || !this.sizeBtns.length || this.colorOptionIndex < 0) return;

    this.sizeBtns.forEach((btn) => {
      const options = JSON.parse(btn.getAttribute('data-options') || '[]');
      const colorForThisVariant = options[this.colorOptionIndex];
      const match = colorForThisVariant && colorForThisVariant.trim().toLowerCase() === color.trim().toLowerCase();
      btn.style.display = match ? 'inline-flex' : 'none';
    });

    const visibleBtns = Array.from(this.sizeBtns).filter((b) => b.style.display !== 'none');
    if (visibleBtns.length > 0 && !visibleBtns.some((b) => b.classList.contains('active'))) {
      this.selectSizeBtn(visibleBtns[0]);
    }
  }

  initSizeButtons() {
    this.sizeBtns.forEach((btn) => {
      btn.setAttribute('aria-pressed', btn.classList.contains('active') ? 'true' : 'false');

      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.selectSizeBtn(btn);
        if (this.sizeSelector) this.sizeSelector.classList.remove('active');
        this.ajaxAddToCart(this.activeVariantId, btn);
      });
    });
  }

  selectSizeBtn(btn) {
    this.sizeBtns.forEach((b) => {
      b.classList.remove('active');
      b.setAttribute('aria-pressed', 'false');
    });
    btn.classList.add('active');
    btn.setAttribute('aria-pressed', 'true');

    this.activeSize = btn.getAttribute('data-value') || '';
    this.resolveVariant();
  }

  resolveVariant() {
    if (!this.variants.length) return;

    const selected = [];
    if (this.colorOptionIndex >= 0 && this.activeColor) {
      selected[this.colorOptionIndex] = this.activeColor.trim().toLowerCase();
    }
    if (this.sizeOptionIndex >= 0 && this.activeSize) {
      selected[this.sizeOptionIndex] = this.activeSize.trim().toLowerCase();
    }

    let matched = null;
    for (const variant of this.variants) {
      const opts = variant.options.map((o) => o.trim().toLowerCase());
      let isMatch = true;
      for (let i = 0; i < opts.length; i++) {
        if (selected[i] !== undefined && opts[i] !== selected[i]) {
          isMatch = false;
          break;
        }
      }
      if (isMatch) {
        matched = variant;
        break;
      }
    }

    if (matched) {
      this.activeVariantId = String(matched.id);
      if (this.quickAddBtn) {
        this.quickAddBtn.setAttribute('data-variant-id', String(matched.id));
      }
      this.updateSizeAvailability(matched);
    }
  }

  updateSizeAvailability(currentMatch) {
    if (!this.sizeBtns.length || this.colorOptionIndex < 0) return;

    const selectedColor = this.activeColor.trim().toLowerCase();

    this.sizeBtns.forEach((btn) => {
      const options = JSON.parse(btn.getAttribute('data-options') || '[]');
      const colorForThisVariant = options[this.colorOptionIndex];
      if (!colorForThisVariant || colorForThisVariant.trim().toLowerCase() !== selectedColor) return;

      const candidateOptions = options.map((o) => o.trim().toLowerCase());
      if (this.sizeOptionIndex >= 0) {
        const sizeVal = btn.getAttribute('data-value');
        if (sizeVal) candidateOptions[this.sizeOptionIndex] = sizeVal.trim().toLowerCase();
      }

      const available = this.variants.some((v) => {
        const vOpts = v.options.map((o) => o.trim().toLowerCase());
        return vOpts.every((opt, idx) => opt === candidateOptions[idx]) && v.available;
      });

      btn.classList.toggle('disabled', !available);
      btn.disabled = !available;
      btn.setAttribute('aria-disabled', !available ? 'true' : 'false');
    });
  }

  initQuickAdd() {
    if (!this.quickAddBtn) return;

    this.quickAddBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const hasMultiple = this.quickAddBtn.getAttribute('data-has-multiple-variants') === 'true';
      if (hasMultiple && this.sizeSelector) {
        this.sizeSelector.classList.add('active');
        const firstBtn = this.sizeSelector.querySelector('.wagmade-card__size-btn:not(.disabled):not([disabled])');
        if (firstBtn) firstBtn.focus();
      } else {
        this.ajaxAddToCart(this.activeVariantId, this.quickAddBtn);
      }
    });
  }

  initOutsideClick() {
    document.addEventListener('click', (e) => {
      const isMobile = window.innerWidth <= 749;
      if (isMobile && e.target === this.sizeSelector) {
        this.classList.remove('is-active');
        this.sizeSelector.classList.remove('active');
        return;
      }

      if (!this.contains(e.target)) {
        this.classList.remove('is-active');
        if (this.sizeSelector) {
          this.sizeSelector.classList.remove('active');
        }
      }
    });

    if (this.sizeSelector) {
      this.sizeSelector.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.classList.remove('is-active');
          this.sizeSelector.classList.remove('active');
          this.quickAddBtn?.focus();
        }
      });
    }
  }

  initMediaTouch() {
    this.mediaLink = this.querySelector('.wagmade-card__media');
    if (this.mediaLink) {
      this.mediaLink.addEventListener('click', (e) => {
        const isMobile = window.innerWidth <= 749;
        if (isMobile) {
          if (!this.classList.contains('is-active')) {
            e.preventDefault();
            e.stopPropagation();
            this.classList.add('is-active');

            const hasMultiple = this.quickAddBtn && this.quickAddBtn.getAttribute('data-has-multiple-variants') === 'true';
            if (hasMultiple && this.sizeSelector) {
              this.sizeSelector.classList.add('active');
            }
          }
        }
      });
    }
  }

  async ajaxAddToCart(variantId, buttonEl) {
    if (!variantId) return;

    const originalText = buttonEl.textContent;
    buttonEl.textContent = 'Adding...';
    buttonEl.disabled = true;

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.description || data.message || 'Could not add to cart');
      }

      buttonEl.textContent = 'Added';
      setTimeout(() => {
        buttonEl.textContent = originalText;
        buttonEl.disabled = false;
      }, 1500);

      await this.refreshAndOpenDrawer();
    } catch (error) {
      buttonEl.textContent = 'Error';
      setTimeout(() => {
        buttonEl.textContent = originalText;
        buttonEl.disabled = false;
      }, 1500);
    }
  }

  async refreshAndOpenDrawer() {
    const drawer = document.querySelector('wagmade-cart-drawer');
    if (!drawer) return;

    try {
      await drawer.refreshFromSection('/cart');
    } catch (_) {
      // fallback: try renderSection via sections API
      try {
        const root = window.Shopify?.routes?.root || '/';
        const sectionsResp = await fetch(`${root}?sections=wagmade-cart-drawer`);
        if (sectionsResp.ok) {
          const sections = await sectionsResp.json();
          const html = sections['wagmade-cart-drawer'];
          if (html) drawer.renderSection(html);
        }
      } catch (_2) {
        // silent
      }
    }
    drawer.open();
  }
}

if (!customElements.get('wagmade-product-card')) {
  customElements.define('wagmade-product-card', WagmadeProductCard);
}
